import { useEffect, useRef, useState, ChangeEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { setExchangeRate, setOrigin } from '@/redux/bookingSlice';
import { FunnelType, ICity } from '@/types/types';
import LocationIcon from '@/assets/icons/location.svg?react';
import useClickOutside from '@/hooks/useClickOutside';
import arrowDown from '@/assets/icons/arrow-down.svg';
import PlanSVG from '../planSVG/PlanSVG';
import styles from './selectors.module.scss';
import APIKit, { getPlanAvailableOrigins } from '@/utils/APIKit';
import { setAvailableCities, setPlansInfo } from '@/redux/planSlice';

interface IProps {
    location?: string;
}

const normalizeString = (string: string) => {
    const normalizedString = string
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    return normalizedString;
};

const OriginSelector = ({ location = 'home' }: IProps) => {
    const currentPlanId = useAppSelector((state) => state.plan.currentPlan);
    const origin = useAppSelector((state) => state.booking.origin);
    const currency = useAppSelector((state) => state.booking.currency);
    const destination = useAppSelector((state) => state.booking.destination);
    const funnelType = useAppSelector((state) => state.booking.funnelType);
    const globalAvailableCities = useAppSelector((state) => state.plan.availableCities) ?? [];
    const isSurpriseFunnel = funnelType === FunnelType.surprise;
    const [citiesList, setCitiesList] = useState<ICity[]>([origin]);
    const [isActive, setIsActive] = useState(false);
    const selectorRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const isHomePage = location === 'home';
    const citiesAreLoading = useRef<boolean>(false);
    const plansInfoAreLoading = useRef<boolean>(false);
    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        if (origin.id === 0) return;
        if (plansInfoAreLoading.current) return;

        plansInfoAreLoading.current = true;
        APIKit.get(`/packages/origin/${origin.id}/?target_currency=${currency.name}`)
            .then((res) => {
                dispatch(setPlansInfo(res.data));
                dispatch(setExchangeRate(res.data[0]?.exchange_rate ?? 1));
            })
            .catch((error) => console.log(error))
            .finally(() => {
                plansInfoAreLoading.current = false;
            });
    }, [origin.id, currency.name]);

    useEffect(() => {
        if (citiesAreLoading.current) return;
        citiesAreLoading.current = true;
        getCitiesToShow();
    }, [i18n.language, currentPlanId]);

    const getCitiesToShow = async () => {
        let availableCities: ICity[] = [];

        availableCities = await getPlanAvailableOrigins(currentPlanId);
        citiesAreLoading.current = false;

        dispatch(setAvailableCities(availableCities));
    };

    useEffect(() => {
        setAvailableCities(globalAvailableCities);
        setCitiesList(globalAvailableCities);
    }, [globalAvailableCities?.length]);

    useEffect(() => {
        setCurrentOrigin();
    }, [globalAvailableCities]);

    useEffect(() => {
        if (isActive) inputRef.current?.focus();
    }, [isActive]);

    useClickOutside(selectorRef, () => {
        if (isActive) {
            setCitiesList(globalAvailableCities);
            setIsActive(false);
        }
    });

    const setCurrentOrigin = () => {
        const originInList = globalAvailableCities?.find((city) => origin.id === city.id);
        if (originInList) {
            dispatch(setOrigin(originInList));
            return;
        }

        if (globalAvailableCities?.length > 0 && isSurpriseFunnel) {
            const barcelonaID = 2314;
            const defaultCity = globalAvailableCities.find((city) => city.id === barcelonaID);
            const originToSet = defaultCity ?? globalAvailableCities[0];
            dispatch(setOrigin(originToSet));
        }
    };

    const onSelectorClick = (e: MouseEvent<HTMLButtonElement>) => {
        const currentTarget = e.target;
        const inputElement = inputRef?.current;
        const eventOutsideInput = currentTarget !== inputElement;
        const eventWasTriggerByKeyboard = e.detail === 0;

        if (eventOutsideInput && !eventWasTriggerByKeyboard) {
            return setIsActive(!isActive);
        }
        if (isActive) return false;

        return setIsActive(true);
    };

    const onItemClick = (city: ICity) => {
        dispatch(setOrigin(city));
        setIsActive(false);
        setCitiesList(globalAvailableCities);
    };

    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const inputValue = normalizeString(e.target.value);

        if (inputValue === '') return setCitiesList(globalAvailableCities);

        const newCitiesList = globalAvailableCities.filter((city: ICity) => {
            const cityName = normalizeString(city.name);
            const cityCountry = normalizeString(city.countryName);

            const cityMatchInput = cityName.includes(inputValue) || cityCountry.includes(inputValue);

            return cityMatchInput;
        });

        return setCitiesList(newCitiesList);
    };

    const labelText = isHomePage ? t('origin') : t('whereFrom');
    const countryName = origin.countryName !== '' ? `, ${origin.countryName}` : '';
    const selectorText = `${origin.name}${countryName}`;
    const selectorClassName = isActive ? `${styles.selector} ${styles.selectorActive}` : styles.selector;
    const containerStyles = isHomePage
        ? `${styles.selectorContainer} ${styles.originSelector}`
        : `${styles.selectorContainer} ${styles.originSelector} ${styles.fullWidth}`;

    const citiesToShow = isSurpriseFunnel ? citiesList : citiesList.filter((city) => city.id !== destination?.id);

    return (
        <div className={containerStyles} ref={selectorRef}>
            <p className={styles.selectorName}>{labelText}</p>
            <button className={selectorClassName} onClick={(e) => onSelectorClick(e)} aria-label={t('origin')}>
                <PlanSVG svg={<LocationIcon />} className={styles.icon} />
                {isActive && <input className={styles.input} onChange={onInputChange} ref={inputRef} />}
                {!isActive && <p className={styles.text}>{selectorText}</p>}
                <div className={styles.arrow}>
                    <img src={arrowDown} alt="" className={isActive ? styles.arrowActive : ''} />
                </div>
            </button>
            {isActive && <SelectorOptions cities={citiesToShow} onItemClick={onItemClick} />}
        </div>
    );
};

interface IOptionsProps {
    cities: ICity[];
    onItemClick: any;
}

const SelectorOptions = ({ cities, onItemClick }: IOptionsProps) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;

    const citiesByCountryName = [...cities].sort(
        (a, b) =>
            a.countryName.charCodeAt(0) - b.countryName.charCodeAt(0) || a.name.charCodeAt(0) - b.name.charCodeAt(0),
    );

    const putFirstTheLanguageCities = () => {
        const selectedLanguageCities = citiesByCountryName.filter((city) => city.countryCode.toLowerCase() === lang);
        const otherCities = citiesByCountryName.filter((city) => city.countryCode.toLowerCase() !== lang);

        const newCitiesList = [...selectedLanguageCities, ...otherCities];
        return newCitiesList;
    };

    const citiesOrderedByLanguage = putFirstTheLanguageCities();

    return (
        <div className={`${styles.selectorOptions} ${styles.originSelectorOptions}`}>
            <ul>
                {cities.length > 0 ? (
                    citiesOrderedByLanguage?.map((city: ICity, index: number) => {
                        const countryName = city.countryName !== '' ? `, ${city.countryName}` : '';

                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={`${city.id}${city.name}${index}`}>
                                <button onClick={() => onItemClick(city)}>
                                    <PlanSVG svg={<LocationIcon />} className={styles.icon} />
                                    {`${city.name}${countryName}`}
                                </button>
                            </li>
                        );
                    })
                ) : (
                    <span className={styles.notFound}>{t('cityNotFound')}</span>
                )}
            </ul>
        </div>
    );
};

export default OriginSelector;
